/**
 * @fileoverview アプリケーション初期化
 */

import gameState from '../core/game-state.js';
import { persistenceManager } from '../core/persistence-manager.js';
import { getBoardRenderer } from '../graphics/renderers/board-renderer.js';
import { initializeImagePreloader } from '../platform/image-preloader.js';
import {
  initializeServiceWorker,
  onServiceWorkerUpdate,
  applyServiceWorkerUpdate,
} from '../platform/service-worker-manager.js';
import { visibilityManager } from '../platform/visibility-manager.js';
import { createServiceWorkerUpdateBanner } from '../presentation/components/update-banner.js';
import { initializeButtonController } from '../presentation/controllers/button-controller.js';
import { initializeLimitController } from '../presentation/controllers/limit-controller.js';
import { initializeResultController } from '../presentation/controllers/result-controller.js';
import { initializeScoreController } from '../presentation/controllers/score-controller.js';
import { addDraggableEvents } from '../presentation/input/interact-adapter.js';
import { menuSystem } from '../presentation/input/menu-controller.js';
import { initializeStartButton } from '../presentation/input/start-button.js';
import { initTutorial } from '../presentation/tutorial/tutorial-orchestrator.js';
import { STATE_PATHS } from '../shared/constants/state-paths.js';
import { MinimalErrorTracker } from '../shared/log.js';

function performTeardown(teardownFunctions) {
  [...teardownFunctions].reverse().forEach((fn) => {
    try {
      if (typeof fn === 'function') {
        fn();
      }
    } catch (error) {
      MinimalErrorTracker.warn('TEARDOWN', `Teardown function failed: ${error.message}`);
    }
  });
}

/**
 * 開発環境用プラットフォーム初期化
 * Service Workerとキャッシュのクリーンアップ
 */
async function initDevPlatform() {
  try {
    if (typeof __PRODUCTION__ !== 'undefined' && !__PRODUCTION__ && 'serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        try {
          await registration.unregister();
        } catch (error) {
          MinimalErrorTracker.info('SW', `SW unregister failed: ${error.message}`);
        }
      }

      if (window.caches && caches.keys) {
        try {
          const names = await caches.keys();
          await Promise.all(
            names.map((name) =>
              name.startsWith('match3-vanilla-') ? caches.delete(name) : Promise.resolve(false),
            ),
          );
        } catch (error) {
          MinimalErrorTracker.info('SW', `Cache clear failed: ${error.message}`);
        }
      }
    }
  } catch (error) {
    MinimalErrorTracker.info('SW', `SW disable failed: ${error.message}`);
  }
}

/**
 * アセット初期化フェーズ
 * 画像プリローダーの初期化と結果処理
 */
async function initAssets() {
  const assetsReady = await initializeImagePreloader();
  if (!assetsReady) {
    try {
      const { getImageStats } = await import('../platform/image-preloader.js');
      const stats = getImageStats();
      const successRate = stats.successCount / stats.total;

      MinimalErrorTracker.warn(
        'ASSETS',
        `CSS images failed to load (${Math.round(successRate * 100)}% success rate). ` +
          `Game will continue with degraded visuals. Failed: ${stats.failureCount} images`,
        { successRate, degradedFeatures: ['piece-graphics'] },
      );
    } catch (error) {
      MinimalErrorTracker.warn(
        'ASSETS',
        'CSS images failed to load, continuing with degraded experience',
      );
    }
  }
}

/**
 * GameStateと永続化の初期化フェーズ
 */
async function initGameStateAndPersistence() {
  if (!gameState || typeof gameState.getState !== 'function') {
    throw new Error('GameState is not properly initialized');
  }

  const state = gameState.getState();
  if (!state || !state.core) {
    throw new Error('GameState state is invalid');
  }

  // 永続化データの初期ロード
  try {
    const settings = persistenceManager.loadSettings();
    if (settings) {
      gameState.setState(STATE_PATHS.SETTINGS, settings, 'persistence_load');
    }

    const stats = persistenceManager.loadStats();
    if (stats) {
      gameState.setState(STATE_PATHS.STATS, stats, 'persistence_load');
    }

    MinimalErrorTracker.info('PERSISTENCE', 'Persistence data loaded successfully');
  } catch (error) {
    MinimalErrorTracker.warn('PERSISTENCE', `Failed to load persistence data: ${error.message}`);
  }

  // デバウンス付き自動保存の設定
  const AUTOSAVE_DEBOUNCE_MS = 100;
  let saveTimer = null;

  const unsubscribe = gameState.subscribe((event) => {
    // settings.* または stats.* の変更のみ監視
    // Note: プレフィックスマッチングは意図的に文字列リテラル使用（パターンマッチの明確化）
    if (!event.path?.startsWith('settings.') && !event.path?.startsWith('stats.')) {
      return;
    }

    // デバウンス処理：連続変更時は最後の変更のみ保存
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        if (event.path.startsWith('settings.')) {
          const currentSettings = gameState.getState(STATE_PATHS.SETTINGS);
          persistenceManager.saveSettings(currentSettings);
        } else if (event.path.startsWith('stats.')) {
          const currentStats = gameState.getState(STATE_PATHS.STATS);
          persistenceManager.saveStats(currentStats);
        }
      } catch (error) {
        MinimalErrorTracker.warn('PERSISTENCE', `Auto-save failed: ${error.message}`);
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  });

  MinimalErrorTracker.info('PERSISTENCE', 'Auto-save monitoring enabled (100ms debounce)');

  // teardown関数を返す
  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
    if (saveTimer) {
      clearTimeout(saveTimer);
    }
    MinimalErrorTracker.info('PERSISTENCE', 'Auto-save monitoring disabled');
  };
}

/**
 * プラットフォーム初期化フェーズ
 * ページ可視性管理など、基本的なプラットフォーム機能を初期化
 */
async function initPlatform() {
  // VisibilityManager初期化
  visibilityManager.initialize();

  // 可視性ハンドリングはドライバ初期化時に実施（game-controls.jsで）

  MinimalErrorTracker.info('PLATFORM', 'Visibility management initialized');

  return () => {
    visibilityManager.destroy();
  };
}

/**
 * UIコントローラー初期化フェーズ
 */
async function initUIControllers() {
  const uiUnsubscribes = [
    initializeButtonController(gameState),
    initializeLimitController(gameState),
    initializeResultController(gameState),
    initializeScoreController(gameState),
  ];

  return () => {
    uiUnsubscribes.forEach((unsubscribe) => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
  };
}

/**
 * レンダリングシステム初期化フェーズ
 */
async function initRendering() {
  // Board要素取得（DOM参照はここで1回のみ）
  const boardElement = document.getElementById('board');
  if (!boardElement) {
    throw new Error('Board element not found');
  }

  // BoardRendererに依存注入
  const renderer = getBoardRenderer();
  renderer.initialize(boardElement);

  const { initializeAnimationHandlers } = await import(
    '../graphics/animation/animation-coordinator.js'
  );
  initializeAnimationHandlers();
  MinimalErrorTracker.info('RENDER', 'Animation handlers initialized');
}

/**
 * 入力コントロール初期化フェーズ
 */
async function initControls() {
  initializeStartButton();
  addDraggableEvents();
  menuSystem.initialize();

  // スキルボタンアイコンの初期化
  const { updateSkillButtonIcon } = await import('../presentation/helpers/icon-renderer.js');
  const settings = gameState.getState(STATE_PATHS.SETTINGS);
  const selectedSkill = settings?.selectedSkill || 'area5x5';
  updateSkillButtonIcon(selectedSkill);

  // 設定変更時にスキルボタンアイコンを更新
  const unsubscribe = gameState.subscribe((event) => {
    if (event.path === 'settings.selectedSkill') {
      const newSkillType = event.value;
      if (newSkillType) {
        updateSkillButtonIcon(newSkillType);
      }
    }
  });

  MinimalErrorTracker.info('CONTROLS', 'Skill button icon initialized and listener attached');

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

/**
 * FXシステム事前初期化フェーズ
 */
async function prewarmFX() {
  const boardElement = document.getElementById('board');
  if (boardElement) {
    const { preInitializeFX } = await import('../graphics/effects/shared/shared.js');
    // 非同期で事前初期化（エラーは内部でハンドル）
    preInitializeFX(boardElement).catch((error) => {
      MinimalErrorTracker.warn('FX', `FX system pre-initialization failed: ${error.message}`);
    });
  }
}

/**
 * 本番環境用Service Worker初期化フェーズ
 */
async function initServiceWorkerProd() {
  if (typeof __PRODUCTION__ !== 'undefined' && __PRODUCTION__) {
    const swInitialized = await initializeServiceWorker();
    if (!swInitialized) {
      MinimalErrorTracker.warn('SW', 'Service Worker initialization failed or not supported');
      return undefined;
    }

    const banner = createServiceWorkerUpdateBanner({
      onReload: async () => {
        const applied = applyServiceWorkerUpdate();
        if (!applied) {
          MinimalErrorTracker.warn('SW', 'No waiting Service Worker available for activation');
          return;
        }
        banner.hide();
      },
    });

    const unsubscribe = onServiceWorkerUpdate(() => {
      banner.show();
    });

    return () => {
      unsubscribe();
      banner.destroy();
    };
  }

  return undefined;
}

/**
 * レガシー互換API - 新しいteardown関数に委譲
 * @deprecated initApp戻り値のteardownを使用してください
 */
export function teardownUIControllers() {
  console.warn('teardownUIControllers is deprecated. Use initApp().teardown instead.');
}

/**
 * レガシー互換API - 新しいteardown関数に委譲
 * @deprecated initApp戻り値のteardownを使用してください
 */
export function teardownApp() {
  console.warn('teardownApp is deprecated. Use initApp().teardown instead.');
  // 永続化のteardownは各フェーズのteardown関数で自動的に処理されます
}

/**
 * アプリケーション初期化関数（オーケストレーター）
 * @returns {Promise<{success: boolean, duration: number, teardown?: Function, error?: string}>} 初期化結果
 */
export async function initApp() {
  const startTime = performance.now();
  const teardownFunctions = [];

  try {
    MinimalErrorTracker.lifecycle('Application initialization started');

    const runStep = async (logTag, name, fn, { fatal = false } = {}) => {
      try {
        const result = await fn();
        if (typeof result === 'function') {
          teardownFunctions.push(result);
        }
      } catch (error) {
        if (fatal) {
          MinimalErrorTracker.critical(
            logTag,
            `${name} initialization failed: ${error.message}`,
            error,
          );
          throw error;
        }

        MinimalErrorTracker.warn(logTag, `${name} initialization failed: ${error.message}`);
      }
    };

    await runStep('DEV_PLATFORM', 'Dev platform', initDevPlatform);
    await runStep('ASSETS', 'Assets', initAssets);
    await runStep('PLATFORM', 'Platform', initPlatform);
    await runStep('GAME_STATE', 'Game state', initGameStateAndPersistence, { fatal: true });
    await runStep('UI_CONTROLLERS', 'UI controllers', initUIControllers);
    await runStep('RENDERING', 'Rendering', initRendering);
    await runStep('CONTROLS', 'Controls', initControls);
    await runStep('TUTORIAL', 'Onboarding tutorial', initTutorial);
    await runStep('FX', 'FX prewarm', prewarmFX);
    await runStep('SERVICE_WORKER', 'Service worker', initServiceWorkerProd);

    const duration = Math.round(performance.now() - startTime);
    MinimalErrorTracker.lifecycle('Application initialized successfully', `${duration}ms`);

    // teardown関数を作成（逆順で実行、永続化も含む）
    const teardown = () => performTeardown(teardownFunctions);

    return {
      success: true,
      duration,
      teardown,
    };
  } catch (error) {
    // エラー発生時も部分的なteardownを実行
    performTeardown(teardownFunctions);

    const duration = Math.round(performance.now() - startTime);
    MinimalErrorTracker.critical(
      'INIT_APP',
      `Application initialization failed after ${duration}ms`,
      error,
    );

    return {
      success: false,
      duration,
      error: error.message,
    };
  }
}
